import React, { useEffect, useState } from 'react';
import {
  Button,
  useMediaQuery,
  ButtonGroup,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import objectStore from '../stores/objectStore';
import TagSelector from './settings/tags/TagSelector';
import { observer } from 'mobx-react-lite';
import FileEditUpload from './FileEditUpload';
import { config } from '../config';
import logger from '../utils/logger';
import statusStore from '../stores/statusStore';

const DynamicFieldsEdit = React.forwardRef(({ object, fields }, ref) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  const [initialValues, setInitialValues] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [selectedTags, setSelectedTags] = useState([]);
  const [errors, setErrors] = useState({});
  const [wasValidated, setWasValidated] = useState(false);

  // const [address, setAddress] = useState('') // Стейт для адреса

  const [address, setAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  function setFieldsFromDynamicFilelds() {}

  // Стейт для значений всех полей
  useEffect(() => {
    // Здесь собираем начальные значения для всех полей
    const init = {};
    Object.keys(fields).forEach((key) => {
      // Если значение есть на верхнем уровне, используем его,
      // иначе ищем в details или задаем пустую строку.
      init[key] =
        object[key] !== undefined ? object[key] : (object.details && object.details[key]) || '';
    });
    setInitialValues(init);
    setFieldValues(init);
    
    // Инициализируем теги из object.details.tags, если они существуют
    if (object.details && object.details.tags) {
      // Здесь убедитесь, что формат тегов соответствует ожидаемому в TagSelector
      // Tags are referenced by organization-scoped id; TagSelector supplies display fields.
      // Получим полные данные тегов из statusStore, используя id из object.details.tags
      const fullTags = object.details.tags.map(tag => {
        const fullTag = statusStore.tags.find(storeTag => storeTag.id === tag.id);
        return fullTag || tag; // Если тег не найден в хранилище, используем исходный
      });
      
      setSelectedTags(fullTags);
    }
  }, [object, fields]);

  const handleSuggestionClick = (suggestion) => {
    setAddress(suggestion.value);
    setSelectedAddress(suggestion.data);
    setShowSuggestions(false);

    // Передаём данные в родительский компонент
    setFieldsFromDynamicFilelds((prev) => ({
      ...prev,
      address: suggestion.value, // строка адреса
      addressData: suggestion.data, // структурированные данные
    }));
  };
  // Обработчик для текстовых полей, текстовых областей и select
  const handleChange = (key, value) => {
    // Просто сохраняем значение как есть, без преобразования типа
    setFieldValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const shouldDisplayField = (field, values) => {
		const { dependencies, hideWhen } = field

		// 1. Check dependencies (SHOW logic - OR for array format, AND for object format)
		let showBasedOnDependencies = true
		if (dependencies) {
			const checkCondition = (depField, depValues) => {
				const actualValue = values[depField]
				if (Array.isArray(actualValue)) {
					// multiple = true
					return actualValue.some(val => depValues.includes(val))
				}
				// multiple = false (single value)
				return depValues.includes(actualValue)
			}

			if (Array.isArray(dependencies)) {
				// Old array format (OR logic between conditions)
				showBasedOnDependencies = dependencies.some(condition =>
					Object.entries(condition).every(([depField, depValues]) =>
						checkCondition(depField, depValues)
					)
				)
			} else {
				// New object format (AND logic between conditions)
				showBasedOnDependencies = Object.entries(dependencies).every(
					([depField, depValues]) => checkCondition(depField, depValues)
				)
			}
		}

		// If dependencies condition says hide, then hide.
		if (!showBasedOnDependencies) {
			return false
		}

		// 2. Check hideWhen (HIDE if ALL conditions met - AND logic)
		if (hideWhen) {
			const allHideConditionsMet = Object.entries(hideWhen).every(
				([depField, depValues]) => {
					const actualValue = values[depField]
					// Check if the actual value matches *any* of the values specified for hiding in depValues
					if (Array.isArray(actualValue)) {
						// multiple = true
						// If *any* selected value is in the hide list for this field, this condition is true
						return actualValue.some(val => depValues.includes(val))
					}
					// multiple = false (single value)
					return depValues.includes(actualValue) // If the single value is in the hide list, this condition is true
				}
			)

			// If all hide conditions were met, hide the field.
			if (allHideConditionsMet) {
				return false
			}
		}

		// If we reached here, dependencies allow showing and hideWhen conditions were not all met.
		return true
	}

  // Группируем поля по group
  const groupedFields = Object.entries(fields).reduce((acc, [key, field]) => {
    const group = field.group || key; // Если group не задан, используем ключ как отдельную группу
    if (!acc[group]) acc[group] = [];
    acc[group].push({ key, ...field });
    return acc;
  }, {});

  // Обработчик для переключения значений кнопок
  const handleToggle = (key, optionId, multiple) => {
    setFieldValues((prev) => {
      if (multiple) {
        // Предыдущее значение (массив ID)
        const selectedIds = prev[key] || [];
        // Если в массиве уже есть optionId, убираем его, иначе добавляем
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];

        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        // Одиночное значение
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });

    // Если нужно, аналогично меняем setFieldsFromDynamicFilelds(...)
    setFieldsFromDynamicFilelds((prev) => {
      if (multiple) {
        const selectedIds = prev[key] || [];
        const newValue = selectedIds.includes(optionId)
          ? selectedIds.filter((id) => id !== optionId)
          : [...selectedIds, optionId];
        return {
          ...prev,
          [key]: newValue,
        };
      } else {
        return {
          ...prev,
          [key]: optionId,
        };
      }
    });
  };

  // Функция для проверки всех обязательных полей
  const validateRequiredFields = () => {
    const newErrors = {};
    let isValid = true;
    
    // Проходим по всем полям
    Object.entries(fields)
      .filter(([key, field]) => shouldDisplayField(field, fieldValues)) // Только видимые поля
      .forEach(([key, field]) => {
        // Проверяем только поля с required: true
        if (field.required) {
          let fieldValid = true;
          
          // Проверка для разных типов полей
          if (field.controlType === 'buttons' || field.controlType === 'dropdown') {
            if (field.multiple) {
              // Если поле множественного выбора, проверяем что массив не пустой
              fieldValid = Array.isArray(fieldValues[key]) && fieldValues[key].length > 0;
            } else {
              // Если поле одиночного выбора, проверяем что значение существует
              fieldValid = fieldValues[key] !== undefined && fieldValues[key] !== null;
            }
          } else if (field.controlType === 'text' || field.controlType === 'textarea') {
            // Для текстовых полей проверяем наличие и не пустоту строки
            fieldValid = fieldValues[key] !== undefined && 
                       fieldValues[key] !== null && 
                       String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'number' || field.controlType === 'float') {
            // Для числовых полей проверяем наличие значения
            fieldValid = fieldValues[key] !== undefined && 
                       fieldValues[key] !== null && 
                       String(fieldValues[key]).trim() !== '';
          } else if (field.controlType === 'increment') {
            // Для increment проверяем, что значение больше 0
            fieldValid = fieldValues[key] !== undefined && 
                       fieldValues[key] !== null && 
                       fieldValues[key] > 0;
          }
          
          // Если поле не прошло валидацию
          if (!fieldValid) {
            newErrors[key] = 'Это поле обязательно для заполнения';
            isValid = false;
          }
        }
      });
    
    // Обновляем состояние ошибок
    setErrors(newErrors);
    setWasValidated(true);
    
    return isValid;
  };

  const handleSubmit = () => {
    logger.log('Функция в дочернем компоненте вызвана!');
    
    // Проверяем заполнение обязательных полей перед отправкой
    if (!validateRequiredFields()) {
      logger.error('Не все обязательные поля заполнены');
      // Вместо алерта просто выделяем поля с ошибками
      // alert('Пожалуйста, заполните все обязательные поля');
      return false; // Возвращаем false, если валидация не прошла
    }
    
    const patch = Object.keys(fieldValues).reduce((acc, key) => {
      const field = fields[key];

      // Skip dependent fields that don't meet dependencies
      if (field && field.dependencies && !shouldDisplayField(field, fieldValues)) {
        if (String(fieldValues[key]) !== '') {
          acc[key] = '';
        }
        return acc;
      }

      // Only include changed values
      if (String(fieldValues[key]) !== String(initialValues[key])) {
        // Ensure proper type conversion ONLY when submitting
        if (field.controlType === 'number' && fieldValues[key] !== '') {
          const intValue = parseInt(fieldValues[key], 10);
          acc[key] = isNaN(intValue) ? 0 : intValue;
        } else if (field.controlType === 'float' && fieldValues[key] !== '') {
          const floatValue = parseFloat(fieldValues[key]);
          acc[key] = isNaN(floatValue) ? 0 : floatValue;
        } else {
          acc[key] = fieldValues[key];
        }
      }
      return acc;
    }, {});

    patch.tags = selectedTags;

    logger.log('Patch для отправки:', patch);
    const objectId = object.id;
    objectStore.editObject(objectId, patch);
    
    return true; // Возвращаем true, если все прошло успешно
  };

  // Экспонируем функцию через ref
  React.useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

 
  return (
    <div>
      {import.meta.env.VITE_SHOW_COMPONENT_NAME === 'true' && <div onClick={() => logger.log(JSON.stringify(object, null, 2))}>% DynamicFieldsEdit %</div>}

      

      <TagSelector selectedTags={selectedTags} onChange={setSelectedTags} />

      <Box sx={{ marginBottom: 2, position: 'relative', mt: 4 }}>
        <TextField
          label="Адрес"
          size="small"
          fullWidth
          sx={{ maxWidth: '500px' }}
          value={object.address || ''}
          disabled
        />
      </Box>

      {Object.entries(fields)
        .filter(([key, field]) => shouldDisplayField(field, fieldValues)) // Фильтруем поля по зависимостям
        .map(([key, field]) => {
          // Обработка фотографий
          if (field.controlType === 'photo') {
            return (
              <Box key={key} sx={{ marginBottom: 2 }}>
                <FileEditUpload 
                  title={field} 
                  objectData={object} 
                />
              </Box>
            );
          }
          
          switch (field.controlType) {
            case 'text':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="text"
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'number':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="number"
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'float':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <div className="flex flex-row">
                    <TextField
                      label={field.label}
                      size="small"
                      required={field.required}
                      type="number"
                      inputProps={{ step: '0.01' }}
                      fullWidth={isSmallScreen}
                      sx={{
                        maxWidth: isSmallScreen ? '100%' : '500px',
                        width: '100%',
                        alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                      }}
                      value={fieldValues[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      error={!!errors[key]}
                      helperText={errors[key]}
                    />
                    {field.required && (
                      <div className="text-orange-600 font-extrabold text-2xl">*</div>
                    )}
                  </div>
                </Box>
              );
            case 'empty_divider':
              return <div className="mt-12"></div>;
            case 'header':
              return (
                <div className="mt-10 mb-4">
                  <p className="text-xl font-semibold ">{field.title}</p>
                </div>
              );
            case 'header2':
              return (
                <div className="mt-7">
                  <p className="text-base font-normal">{field.title}</p>
                </div>
              );
            case 'increment':
              return (
                <Box
                  key={key}
                  sx={{
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                  <span className="border p-1 border-gray-400 rounded">
                    <label className="ml-2">{field.label}</label>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = Math.max(0, (fieldValues[key] || 0) - 1);
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                        marginBottom: '4px',
                        marginLeft: '20px',
                      }}>
                      -
                    </Button>
                    <span>{fieldValues[key] || 0}</span>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => {
                        const newValue = (fieldValues[key] || 0) + 1;
                        handleChange(key, newValue);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        lineHeight: '1', // Выровнять высоту текста
                        padding: '0 6px', // Добавить симметричные отступы
                        minWidth: '40px', // Зафиксировать минимальную ширину для стабильности
                      }}>
                      +
                    </Button>
                  </span>
                  {field.required && (
                    <div className="text-orange-500 font-extrabold text-xl">*</div>
                  )}
                </Box>
              );

            case 'textarea':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    label={field.label}
                    required={field.required}
                    multiline
                    rows={field.rows || 4}
                    value={fieldValues[key] || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    placeholder={field.placeholder || ''}
                    fullWidth={isSmallScreen}
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px',
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                    }}
                    error={!!errors[key]}
                    helperText={errors[key]}
                  />
                  {field.required && <div className="text-orange-500 font-extrabold text-xl">*</div>}
                </Box>
              );
            case 'dropdown':
              return (
                <Box key={key} sx={{ marginBottom: 2 }}>
                  <TextField
                    select
                    label={field.label}
                    size="small"
                    required={field.required}
                    fullWidth={isSmallScreen}
                    sx={{
                      maxWidth: isSmallScreen ? '100%' : '500px',
                      width: '100%',
                      alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
                    }}
                    SelectProps={{
                      multiple: field.multiple,
                    }}
                    value={fieldValues[key] ?? (field.multiple ? [] : '')}
                    onChange={(e) => {
                      handleChange(key, e.target.value);
                    }}
                    error={!!errors[key]}
                    helperText={errors[key]}
                  >
                    {field.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              );

            case 'buttons':
              // Проверяем, является ли поле _mainDealType
              if (key === '_mainDealType') {
                // Рендерим кнопки, но делаем их неактивными
                return (
                  <Box key={key} sx={{ marginBottom: 2, marginTop: 3 }}>
                    <label>
                      {field.label}
                    </label>
                    <div className="flex flex-row">
                      <div>
                        <ButtonGroup
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            marginTop: '8px',
                            marginRight: '8px',
                            textTransform: 'none',
                          }}
                          variant="outlined"
                          disabled={true} // Делаем все кнопки неактивными
                        >
                          {field.options.map((option) => {
                            const isSelected = field.multiple
                              ? (fieldValues[key] || []).includes(option.id)
                              : fieldValues[key] === option.id;

                            return (
                              <Button
                                key={option.id}
                                variant={isSelected ? 'contained' : 'outlined'}
                                sx={{ 
                                  textTransform: 'none',
                                  opacity: 0.7, // Делаем кнопки более серыми
                                }}
                              >
                                {option.value}
                              </Button>
                            );
                          })}
                        </ButtonGroup>
                      </div>
                    </div>
                  </Box>
                );
              }
              
              return (
                <Box key={key} sx={{ marginBottom: 2, marginTop: 3 }}>
                  <label>
                    {field.label}
                    {/* 	{field.required && ' *'} */}
                  </label>

                  <div className="flex flex-row">
                    <div>
                      <ButtonGroup
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          marginTop: '8px',
                          marginRight: '8px',
                          textTransform: 'none',
                        }}
                        variant="outlined"
                      >
                        {field.options.map((option) => {
                          const isSelected = field.multiple
                            ? (fieldValues[key] || []).includes(option.id)
                            : fieldValues[key] === option.id;

                          return (
                            <Button
                              key={option.id}
                              variant={isSelected ? 'contained' : 'outlined'}
                              sx={{ textTransform: 'none' }}
                              onClick={() => handleToggle(key, option.id, field.multiple)}
                            >
                              {option.value}
                            </Button>
                          );
                        })}
                      </ButtonGroup>
                      {errors[key] && (
                        <FormHelperText error>{errors[key]}</FormHelperText>
                      )}
                    </div>
                    {field.required && (
                      <div className="text-orange-500 font-extrabold text-xl">*</div>
                    )}
                  </div>
                </Box>
              );

            case 'checkbox':
              return (
                <Box key={key} sx={{ marginBottom: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!fieldValues[key]}
                        onChange={(e) => handleChange(key, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                  {errors[key] && (
                    <FormHelperText error>{errors[key]}</FormHelperText>
                  )}
                </Box>
              );
            default:
              return null;
          }
        })}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '10vh',
          paddingBottom: '70px',
        }}>
        {wasValidated && Object.keys(errors).length > 0 && (
        <Box sx={{ 
          padding: 2, 
          marginBottom: 2, 
          backgroundColor: '#FEE2E2', 
          borderRadius: 1, 
          color: '#B91C1C'
        }}>
          <Typography variant="body1" fontWeight="medium">
            Пожалуйста, заполните все обязательные поля, отмеченные звездочкой (*).
          </Typography>
        </Box>
      )}
      </div>
    </div>
  );
});

DynamicFieldsEdit.displayName = 'DynamicFieldsEdit';

export default observer(DynamicFieldsEdit);
